import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  Settings,
  CreditCard,
  Shield,
  Eye,
  EyeOff,
  Save,
  TestTube,
  CheckCircle,
  AlertCircle,
  Loader
} from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { useToast } from '../contexts/ToastContext';
import api from '../services/api';

const Container = styled.div`
  padding: 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0;
`;

const SettingsGrid = styled.div`
  display: grid;
  gap: 24px;
`;

const SettingCard = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e1e5e9;
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e1e5e9;
  background: #f8f9fa;
`;

const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CardContent = styled.div`
  padding: 20px;
`;

const SettingRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr auto;
  gap: 16px;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    align-items: flex-start;
  }
`;

const SettingLabel = styled.div`
  font-weight: 600;
  color: #333;
`;

const SettingDescription = styled.div`
  font-size: 14px;
  color: #666;
  margin-top: 4px;
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #00d4aa;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background: #f5f5f5;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #00d4aa;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 32px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'success' | 'warning' }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  cursor: pointer;

  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
          color: white;
          &:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
          }
        `;
      case 'success':
        return `
          background: #10b981;
          color: white;
          &:hover:not(:disabled) {
            background: #059669;
          }
        `;
      case 'warning':
        return `
          background: #f59e0b;
          color: white;
          &:hover:not(:disabled) {
            background: #d97706;
          }
        `;
      default:
        return `
          background: #f8f9fa;
          color: #333;
          border: 1px solid #e1e5e9;
          &:hover:not(:disabled) {
            background: #e9ecef;
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const StatusIndicator = styled.div<{ $status: 'success' | 'error' | 'warning' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;

  ${props => {
    switch (props.$status) {
      case 'success':
        return `
          background: #d1fae5;
          color: #065f46;
        `;
      case 'error':
        return `
          background: #fee2e2;
          color: #991b1b;
        `;
      case 'warning':
        return `
          background: #fef3c7;
          color: #92400e;
        `;
    }
  }}
`;

const HelpCard = styled.div`
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
`;

const HelpTitle = styled.h4`
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 700;
  color: #9a3412;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HelpList = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: #9a3412;
  font-size: 14px;
`;

const Badge = styled.span<{ $tone?: 'danger' | 'info' }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  margin-left: 8px;

  ${props => {
    if (props.$tone === 'danger') {
      return `
        background: #fee2e2;
        color: #991b1b;
      `;
    }
    return `
      background: #e0f2fe;
      color: #075985;
    `;
  }}
`;

const FieldHint = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 6px;
`;

const WebhookBox = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px dashed #fbbf24;
  background: #fffbeb;
  font-size: 13px;
  color: #92400e;
  word-break: break-all;
`;

interface PaymentSetting {
  id: number;
  key: string;
  value: string;
  description: string;
  is_encrypted: boolean;
  is_active: boolean;
}

export const AdminPaymentSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<PaymentSetting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'success' | 'error' | 'warning' | null>(null);
  const [showValues, setShowValues] = useState<Record<string, boolean>>({});
  const { showSuccess, showError } = useToast();

  const loadSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await api.getPaymentSettings();

      if (response.success) {
        setSettings(response.data);
      } else {
        showError('Erro ao carregar configurações');
      }
    } catch (error: any) {
      showError('Erro ao carregar configurações');
      console.error('Error loading settings:', error);
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const updateSetting = (id: number, value: string) => {
    setSettings(prev => prev.map(setting =>
      setting.id === id ? { ...setting, value } : setting
    ));
  };

  const saveSettings = async () => {
    try {
      setIsSaving(true);

      const settingsToUpdate = settings.map(setting => ({
        id: setting.id,
        value: setting.value
      }));

      const response = await api.updatePaymentSettingsBatch({
        settings: settingsToUpdate
      });

      if (response.success) {
        showSuccess('Configurações salvas com sucesso!');
        await loadSettings();
      } else {
        showError('Erro ao salvar configurações');
      }
    } catch (error: any) {
      showError('Erro ao salvar configurações');
      console.error('Error saving settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const testConnection = async () => {
    try {
      setIsTesting(true);
      const response = await api.testPaymentConnection();

      if (response.success) {
        setConnectionStatus('success');
        showSuccess('Conexão com Mercado Pago estabelecida com sucesso!');
      } else {
        setConnectionStatus('error');
        showError(response.message || 'Falha na conexão com Mercado Pago');
      }
    } catch (error: any) {
      setConnectionStatus('error');
      const errorMessage = error.response?.data?.message || 'Erro ao testar conexão';
      showError(errorMessage);
      console.error('Error testing connection:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setIsTesting(false);
    }
  };

  const toggleShowValue = (settingId: number) => {
    setShowValues(prev => ({
      ...prev,
      [settingId]: !prev[settingId]
    }));
  };

  const getSettingValue = (setting: PaymentSetting) => {
    if (!setting.value) {
      return '';
    }
    if (setting.is_encrypted && !showValues[setting.id]) {
      return '••••••••••••••••';
    }
    return setting.value || '';
  };

  const settingMeta: Record<string, { label: string; hint?: string; required?: boolean }> = {
    mercadopago_access_token: {
      label: 'Access Token (produção)',
      hint: 'Obrigatório para processar pagamentos reais.',
      required: true
    },
    mercadopago_public_key: {
      label: 'Public Key (produção)',
      hint: 'Obrigatória para o checkout no front.',
      required: true
    },
    mercadopago_access_token_sandbox: {
      label: 'Access Token (sandbox)',
      hint: 'Usado apenas em testes.'
    },
    mercadopago_public_key_sandbox: {
      label: 'Public Key (sandbox)',
      hint: 'Usada no checkout em testes.'
    },
    mercadopago_webhook_secret: {
      label: 'Webhook Secret',
      hint: 'Protege a validação dos webhooks.'
    },
    mercadopago_sandbox_mode: {
      label: 'Modo sandbox',
      hint: 'Quando ativo, usa credenciais de teste.'
    },
    payment_processing_active: {
      label: 'Processamento ativo',
      hint: 'Ative para liberar cobranças no sistema.'
    },
    mercadopago_client_id: {
      label: 'Client ID',
      hint: 'Opcional para checkout transparente.'
    },
    mercadopago_client_secret: {
      label: 'Client Secret',
      hint: 'Opcional para checkout transparente.'
    },
    mercadopago_integrator_id: {
      label: 'Integrator ID',
      hint: 'Opcional se você tiver integrador.'
    },
    mercadopago_platform_id: {
      label: 'Platform ID',
      hint: 'Opcional para parcerias/marketplace.'
    },
    mercadopago_checkout_mode: {
      label: 'Modo de checkout',
      hint: 'Escolha o tipo de checkout do Mercado Pago.'
    }
  };

  const settingOrder: Record<string, number> = {
    mercadopago_access_token: 1,
    mercadopago_public_key: 2,
    mercadopago_webhook_secret: 3,
    mercadopago_sandbox_mode: 4,
    payment_processing_active: 5,
    mercadopago_access_token_sandbox: 6,
    mercadopago_public_key_sandbox: 7,
    mercadopago_client_id: 8,
    mercadopago_client_secret: 9,
    mercadopago_integrator_id: 10,
    mercadopago_platform_id: 11,
    mercadopago_checkout_mode: 12
  };

  const sortSettings = (list: PaymentSetting[]) =>
    [...list].sort((a, b) => (settingOrder[a.key] || 999) - (settingOrder[b.key] || 999));

  const getLabel = (setting: PaymentSetting) =>
    settingMeta[setting.key]?.label || setting.description;

  const getHint = (setting: PaymentSetting) =>
    settingMeta[setting.key]?.hint;

  const isRequired = (setting: PaymentSetting) =>
    Boolean(settingMeta[setting.key]?.required);

  const groupedSettings = {
    production: sortSettings(settings.filter(s => !s.key.includes('sandbox') &&
      s.key !== 'mercadopago_sandbox_mode' &&
      s.key !== 'payment_processing_active' &&
      s.key !== 'mercadopago_webhook_secret' &&
      s.key !== 'mercadopago_checkout_mode' &&
      s.key !== 'mercadopago_integrator_id' &&
      s.key !== 'mercadopago_platform_id')),
    sandbox: sortSettings(settings.filter(s => s.key.includes('sandbox'))),
    general: sortSettings(settings.filter(s => s.key === 'mercadopago_sandbox_mode' ||
      s.key === 'payment_processing_active' ||
      s.key === 'mercadopago_webhook_secret')),
    checkout: sortSettings(settings.filter(s => s.key === 'mercadopago_checkout_mode' ||
      s.key === 'mercadopago_integrator_id' ||
      s.key === 'mercadopago_platform_id' ||
      s.key === 'mercadopago_client_id' ||
      s.key === 'mercadopago_client_secret'))
  };

  const missingRequired = settings.filter(setting =>
    isRequired(setting) && !setting.value
  );

  const webhookUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/api/webhooks/mercadopago`
    : '';

  if (isLoading) {
    return (
      <AdminLayout>
        <Container>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
            <Loader size={32} className="animate-spin" />
          </div>
        </Container>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Container>
        <Header>
          <Title>
            <Settings size={32} />
            Configurações de Pagamento
          </Title>
          <Subtitle>
            Gerencie as configurações do Mercado Pago e outros meios de pagamento
          </Subtitle>
        </Header>

        {connectionStatus && (
          <StatusIndicator $status={connectionStatus} style={{ marginBottom: '24px' }}>
            {connectionStatus === 'success' ? (
              <>
                <CheckCircle size={16} />
                Conexão com Mercado Pago ativa
              </>
            ) : (
              <>
                <AlertCircle size={16} />
                Falha na conexão com Mercado Pago
              </>
            )}
          </StatusIndicator>
        )}

        <HelpCard>
          <HelpTitle>
            <AlertCircle size={16} />
            Checklist rapido
          </HelpTitle>
          <HelpList>
            <li>Preencha Access Token e Public Key de produção.</li>
            <li>Ative "Processamento ativo" para liberar cobranças.</li>
            <li>Use "Modo sandbox" apenas para testes.</li>
          </HelpList>
          {webhookUrl && (
            <WebhookBox>
              Webhook URL: {webhookUrl}
            </WebhookBox>
          )}
        </HelpCard>

        {missingRequired.length > 0 && (
          <StatusIndicator $status="warning" style={{ marginBottom: '24px' }}>
            <AlertCircle size={16} />
            Existem campos obrigatorios sem preenchimento.
          </StatusIndicator>
        )}

        <SettingsGrid>
          {/* Configurações Gerais */}
          <SettingCard>
            <CardHeader>
              <CardTitle>
                <Shield size={20} />
                Configurações Gerais
              </CardTitle>
            </CardHeader>
            <CardContent>
              {groupedSettings.general.map(setting => (
                <SettingRow key={setting.id}>
                  <div>
                    <SettingLabel>
                      {getLabel(setting)}
                      {isRequired(setting) && !setting.value && (
                        <Badge $tone="danger">Obrigatorio</Badge>
                      )}
                    </SettingLabel>
                    <SettingDescription>{setting.description}</SettingDescription>
                    {getHint(setting) && <FieldHint>{getHint(setting)}</FieldHint>}
                  </div>

                  <div>
                    {(setting.key === 'mercadopago_sandbox_mode' || setting.key === 'payment_processing_active') ? (
                      <Select
                        value={setting.value || 'false'}
                        onChange={(e) => updateSetting(setting.id, e.target.value)}
                      >
                        <option value="true">Ativo</option>
                        <option value="false">Inativo</option>
                      </Select>
                    ) : (
                      <InputContainer>
                        <Input
                          type={setting.is_encrypted && !showValues[setting.id] ? 'password' : 'text'}
                          value={getSettingValue(setting)}
                          onChange={(e) => updateSetting(setting.id, e.target.value)}
                          placeholder="Digite o valor..."
                        />
                        {setting.is_encrypted && (
                          <ToggleButton
                            type="button"
                            onClick={() => toggleShowValue(setting.id)}
                          >
                            {showValues[setting.id] ? <EyeOff size={16} /> : <Eye size={16} />}
                          </ToggleButton>
                        )}
                      </InputContainer>
                    )}
                  </div>
                </SettingRow>
              ))}
            </CardContent>
          </SettingCard>

          {/* Configurações de Produção */}
          <SettingCard>
            <CardHeader>
              <CardTitle>
                <CreditCard size={20} />
                Mercado Pago - Produção
              </CardTitle>
            </CardHeader>
            <CardContent>
              {groupedSettings.production.map(setting => (
                <SettingRow key={setting.id}>
                  <div>
                    <SettingLabel>
                      {getLabel(setting)}
                      {isRequired(setting) && !setting.value && (
                        <Badge $tone="danger">Obrigatorio</Badge>
                      )}
                    </SettingLabel>
                    <SettingDescription>{setting.description}</SettingDescription>
                    {getHint(setting) && <FieldHint>{getHint(setting)}</FieldHint>}
                  </div>

                  <InputContainer>
                    <Input
                      type={setting.is_encrypted && !showValues[setting.id] ? 'password' : 'text'}
                      value={getSettingValue(setting)}
                      onChange={(e) => updateSetting(setting.id, e.target.value)}
                      placeholder="Digite o valor..."
                    />
                    {setting.is_encrypted && (
                      <ToggleButton
                        type="button"
                        onClick={() => toggleShowValue(setting.id)}
                      >
                        {showValues[setting.id] ? <EyeOff size={16} /> : <Eye size={16} />}
                      </ToggleButton>
                    )}
                  </InputContainer>
                </SettingRow>
              ))}
            </CardContent>
          </SettingCard>

          {/* Configurações de Sandbox */}
          <SettingCard>
            <CardHeader>
              <CardTitle>
                <TestTube size={20} />
                Mercado Pago - Sandbox (Testes)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {groupedSettings.sandbox.map(setting => (
                <SettingRow key={setting.id}>
                  <div>
                    <SettingLabel>{getLabel(setting)}</SettingLabel>
                    <SettingDescription>{setting.description}</SettingDescription>
                    {getHint(setting) && <FieldHint>{getHint(setting)}</FieldHint>}
                  </div>

                  <InputContainer>
                    <Input
                      type={setting.is_encrypted && !showValues[setting.id] ? 'password' : 'text'}
                      value={getSettingValue(setting)}
                      onChange={(e) => updateSetting(setting.id, e.target.value)}
                      placeholder="Digite o valor..."
                    />
                    {setting.is_encrypted && (
                      <ToggleButton
                        type="button"
                        onClick={() => toggleShowValue(setting.id)}
                      >
                        {showValues[setting.id] ? <EyeOff size={16} /> : <Eye size={16} />}
                      </ToggleButton>
                    )}
                  </InputContainer>
                </SettingRow>
              ))}
            </CardContent>
          </SettingCard>

          {/* Configurações de Checkout */}
          <SettingCard>
            <CardHeader>
              <CardTitle>
                <CreditCard size={20} />
                Checkout Transparente
              </CardTitle>
            </CardHeader>
            <CardContent>
              {groupedSettings.checkout.map(setting => (
                <SettingRow key={setting.id}>
                  <div>
                    <SettingLabel>{getLabel(setting)}</SettingLabel>
                    <SettingDescription>{setting.description}</SettingDescription>
                    {getHint(setting) && <FieldHint>{getHint(setting)}</FieldHint>}
                  </div>

                  <div>
                    {setting.key === 'mercadopago_checkout_mode' ? (
                      <Select
                        value={setting.value || 'redirect'}
                        onChange={(e) => updateSetting(setting.id, e.target.value)}
                      >
                        <option value="redirect">Redirect (Padrão)</option>
                        <option value="modal">Modal</option>
                        <option value="transparent">Transparente</option>
                      </Select>
                    ) : (
                      <InputContainer>
                        <Input
                          type={setting.is_encrypted && !showValues[setting.id] ? 'password' : 'text'}
                          value={getSettingValue(setting)}
                          onChange={(e) => updateSetting(setting.id, e.target.value)}
                          placeholder="Digite o valor (opcional)..."
                        />
                        {setting.is_encrypted && (
                          <ToggleButton
                            type="button"
                            onClick={() => toggleShowValue(setting.id)}
                          >
                            {showValues[setting.id] ? <EyeOff size={16} /> : <Eye size={16} />}
                          </ToggleButton>
                        )}
                      </InputContainer>
                    )}
                  </div>
                </SettingRow>
              ))}
            </CardContent>
          </SettingCard>
        </SettingsGrid>

        <ButtonGroup>
          <Button $variant="primary" onClick={saveSettings} disabled={isSaving}>
            {isSaving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
            {isSaving ? 'Salvando...' : 'Salvar Configurações'}
          </Button>

          <Button $variant="warning" onClick={testConnection} disabled={isTesting}>
            {isTesting ? <Loader size={16} className="animate-spin" /> : <TestTube size={16} />}
            {isTesting ? 'Testando...' : 'Testar Conexão'}
          </Button>
        </ButtonGroup>
      </Container>
    </AdminLayout>
  );
};
